import { Reminder } from "@sp-crm/core";
import { RegionToggle } from "components/region-toggle";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { ReminderContext } from "../../../contexts/reminder";
import * as actions from "../../../store/actions";
import * as menuActions from "../../../store/actions/responsive";
import { hideMenu } from "../../../store/actions/responsive";
import { ResponsiveMode } from "../../../store/reducers/responsive";

export const MobileNav: React.FunctionComponent = () => {
    const { mode, showMenu } = useSelector((state: ApplicationState) => state.responsive);
    const dispatch = useDispatch();
    const dismissMenu = React.useCallback(() => {
        dispatch(hideMenu());
    }, [dispatch]);
    if (mode < ResponsiveMode.large) {
        return (
            <Panel
                headerText=""
                isOpen={showMenu}
                isLightDismiss={true}
                type={PanelType.smallFixedNear}
                onDismiss={dismissMenu}>
                <CrmNav />
            </Panel>
        );
    }
    return null;
};

interface NavLink {
    key: string;
    url: string;
    name: string;
}

interface NavAction {
    key: string;
    onClick: () => void;
    name: string;
}

type NavItem = NavAction | NavLink;

const linkClassName =
    "block p-2 text-lg rounded-sm text-gray-700 hover:bg-brand-100 hover:text-brand-800";

const CrmNav: React.FC = () => {
    const smsEnabled = useFeature("sms");
    const dispatch = useDispatch();

    const logout = React.useCallback(() => {
        actions.logout(dispatch);
    }, [dispatch]);

    const closeMenu = React.useCallback(() => {
        dispatch(menuActions.hideMenu());
    }, [dispatch]);

    const getItems = React.useCallback(
        (
            isReminderEnabled: boolean,
            reminders: Reminder[],
            setVisible: (visible: boolean | ((oldState: boolean) => void)) => void,
        ): NavItem[] => {
            const settingsLinks: NavItem[] = [
                {
                    key: "usersettings",
                    url: "/settings",
                    name: "Settings",
                },
            ];

            const result: NavItem[] = [
                {
                    name: "Dashboard",
                    url: "/pipeline",
                    key: "dashboard",
                },
                { name: "Tasks", url: "/tasks", key: "tasks" },
                { name: "Clients", url: "/clients", key: "clients" },
                {
                    name: "Communities",
                    url: "/communities",
                    key: "communities",
                },
                {
                    name: "Referral Sources",
                    url: "/references",
                    key: "references",
                },
                {
                    name: "Files",
                    url: "/files",
                    key: "files",
                },
                { name: "Reports", url: "/reports", key: "reports" },
                ...(smsEnabled
                    ? [{ name: "Messaging", url: "/messaging", key: "messaging" }]
                    : []),
                ...settingsLinks,
                ...(isReminderEnabled
                    ? [
                          {
                              name: `Reminders${
                                  reminders.length > 0 ? ` (${reminders.length})` : ""
                              }`,
                              url: "#",
                              onClick: () => setVisible(visible => !visible),
                              key: "reminder",
                          },
                      ]
                    : []),
                {
                    name: "Log out",
                    onClick: logout,
                    key: "logout",
                },
            ];
            return result;
        },
        [logout, smsEnabled],
    );

    const renderItem = React.useCallback(
        (item: NavItem) => {
            if ((item as NavAction).onClick) {
                const action = item as NavAction;
                const handler: React.MouseEventHandler<HTMLAnchorElement> = e => {
                    e.preventDefault();
                    action.onClick();
                    closeMenu();
                };
                return (
                    <li key={action.key}>
                        <a className={linkClassName} href="#" onClick={handler}>
                            {action.name}
                        </a>
                    </li>
                );
            }
            if ((item as NavLink).url) {
                const link = item as NavLink;
                const handler: React.MouseEventHandler<HTMLAnchorElement> = () =>
                    closeMenu();
                return (
                    <li key={link.key}>
                        <Link className={linkClassName} to={link.url} onClick={handler}>
                            {link.name}
                        </Link>
                    </li>
                );
            }
            console.warn("Missing nav check");
            return null;
        },
        [closeMenu],
    );

    return (
        <ReminderContext.Consumer>
            {({ isReminderEnabled, reminders, setVisible }) => (
                <>
                    <ul className="space-y-1 mb-4">
                        {getItems(isReminderEnabled, reminders, setVisible).map(i =>
                            renderItem(i),
                        )}
                    </ul>
                    <RegionToggle />
                </>
            )}
        </ReminderContext.Consumer>
    );
};
